import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import { useAuth } from './contexts/AuthContext';
import { Layout } from './components/layout/Layout';
import { PublicLayout } from './components/layout/PublicLayout';
import { LoginForm } from './components/auth/LoginForm';

// Public Pages
import { Home } from './pages/public/Home';
import { ClientSignUp } from './pages/public/ClientSignUp';
import { ClientLogin } from './pages/public/ClientLogin';
import { ClientDashboard } from './pages/public/ClientDashboard';

import { RoutesSchedules } from './pages/public/RoutesSchedules';

import { PrivacyPolicy } from './pages/public/PrivacyPolicy';
import { TermsOfService } from './pages/public/TermsOfService';
import { AboutUs } from './pages/public/AboutUs';
import { Careers } from './pages/public/Careers';

// Admin Pages
import { Dashboard } from './pages/Dashboard';
import { Users } from './pages/Users';
import { Routes as RoutesPage } from './pages/Routes';
import { Bookings } from './pages/Bookings';
import { Buses } from './pages/Buses';
import { Analytics } from './pages/Analytics';
import { EmailLogs } from './pages/EmailLogs';
import { Settings } from './pages/Settings';
import { Schedule } from './pages/Schedule';
import { Finance } from './pages/Finance';
import { Promotions } from './pages/Promotions';

import { initializeAdmin } from './setup/adminSetup';
import { quickAdminTest } from './setup/quickAdminTest';
import { emergencyAdminLogin } from './setup/emergencyAdminLogin';
import { autoLogin } from './setup/autoLogin';
import { NotificationService } from './services/notificationService';

const enableAdminAutomation = (import.meta as any).env?.VITE_ENABLE_ADMIN_AUTOMATION === 'true';

// Protected Route Component
function ProtectedRoute({ children, requireAdmin = false }: { children: React.ReactNode, requireAdmin?: boolean }) {
  const { isAuthenticated, user, loading } = useAuth();
  const location = useLocation();

  if (loading) return null;

  if (!isAuthenticated) {
    const loginPath = requireAdmin ? '/admin' : '/login';
    return <Navigate to={loginPath} state={{ from: location }} replace />;
  }

  if (requireAdmin && user?.role === 'client') {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

function App() {
  const { isAuthenticated, loading } = useAuth();

  // Initialize admin user on app startup
  useEffect(() => {
    if (enableAdminAutomation) {
      const initAdmin = async () => {
        try {
          console.log('🚀 Starting admin initialization...');
          const autoLoginResult = await autoLogin();
          if (autoLoginResult.success) {
            console.log('✅ Auto-login successful:', autoLoginResult.message);
          } else {
            console.log('ℹ️ Auto-login failed, trying manual setup...');
            await initializeAdmin();
          }
        } catch (error) {
          console.error('Failed to initialize admin:', error);
        }
      };
      initAdmin();
    }

    if (isAuthenticated) {
      NotificationService.startAllNotifications();
    }

    if (typeof window !== 'undefined' && enableAdminAutomation) {
      (window as any).quickAdminTest = quickAdminTest;
      (window as any).testAdmin = quickAdminTest;
      (window as any).emergencyAdminLogin = emergencyAdminLogin;
      (window as any).autoLogin = autoLogin;
    }

    return () => {
      NotificationService.stopAllNotifications();
    };
  }, [isAuthenticated]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-secondary-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-blue mx-auto"></div>
          <p className="mt-4 text-secondary-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <Routes>
      {/* Public Routes with PublicLayout */}
      <Route element={<PublicLayout />}>
        <Route path="/" element={<Home />} />
        <Route path="/signup" element={<ClientSignUp />} />
        <Route path="/login" element={<ClientLogin />} />
        <Route path="/routes" element={<RoutesSchedules />} />
        <Route path="/about" element={<AboutUs />} />
        <Route path="/careers" element={<Careers />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        
        {/* Client Protected Route */}
        <Route 
          path="/dashboard" 
          element={
            <ProtectedRoute>
              <ClientDashboard />
            </ProtectedRoute>
          } 
        />
      </Route>

      {/* Admin Login (No Layout) */}
      <Route path="/admin" element={isAuthenticated ? <Navigate to="/admin/dashboard" replace /> : <LoginForm />} />

      {/* Admin Protected Routes */}
      <Route 
        path="/admin/dashboard/*" 
        element={
          <ProtectedRoute requireAdmin={true}>
            <Layout>
              <Routes>
                <Route index element={<Dashboard />} />
                <Route path="users" element={<Users />} />
                <Route path="bookings" element={<Bookings />} />
                <Route path="routes" element={<RoutesPage />} />
                <Route path="buses" element={<Buses />} />
                <Route path="analytics" element={<Analytics />} />
                <Route path="emails" element={<EmailLogs />} />
                <Route path="settings" element={<Settings />} />
                <Route path="schedule" element={<Schedule />} />
                <Route path="finance" element={<Finance />} />
                <Route path="promotions" element={<Promotions />} />
                <Route path="*" element={<Navigate to="/admin/dashboard" replace />} />
              </Routes>
            </Layout>
          </ProtectedRoute>
        } 
      />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default App;
